import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, Filter } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { api } from '../../../lib/api';
import { useStandort } from '../../../components/layouts/use-standort';
import { personDetailPath, subprofileDetailPath } from '../../../lib/peopleRoutes';
import { relationshipText } from '../../../lib/subprofileRelationships';
interface PersonRow {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  type?: string;
  roles?: string[];
  parentId?: string; // für Subprofile
  parentName?: string;
  subIndex?: number;
  relationshipText?: string;
  isSubprofile?: boolean;
}

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const SearchResults: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = useQuery();
  const { selectedStandortId } = useStandort();
  const initialQ = query.get('q') || '';
  const initialAllStandorte = ['1', 'true'].includes((query.get('all_standorte') || '').toLowerCase());
  const initialStandortId = query.get('standort_id');

  const [q, setQ] = useState(initialQ);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [searchAllStandorte, setSearchAllStandorte] = useState(initialAllStandorte);
  const [standortId, setStandortId] = useState<string>(initialStandortId || (selectedStandortId ? String(selectedStandortId) : ''));
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState<PersonRow[]>([]);

  useEffect(() => {
    if (standortId || !selectedStandortId) return;
    setStandortId(String(selectedStandortId));
  }, [selectedStandortId, standortId]);

  useEffect(() => {
    const doSearch = async () => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams();
        if (q) params.set('q', q);
        if (typeFilter !== 'all') params.set('type', typeFilter);
        if (searchAllStandorte) params.set('all_standorte', '1');
        else if (standortId) params.set('standort_id', standortId);
        params.set('limit', '50');
        const list = await api.get<any[]>(`/api/personen?${params.toString()}`);
        const mapped: PersonRow[] = (list || []).map((p) => {
          const id = String(p.id ?? p.wpUserId ?? p.ID ?? '');
          const isSubprofile = p.isSubprofile === true;
          const subIndex = Number(p.subIndex || id.split('-').at(-1) || 0) || undefined;
          if (isSubprofile && Number(subIndex || 0) <= 1) {
            return null;
          }
          return {
            id,
            name: p.name ?? p.display_name ?? '',
            email: p.email,
            phone: p.phone,
            type: p.type,
            roles: Array.isArray(p.roles) ? p.roles : [],
            parentId: p.parentId ? String(p.parentId) : undefined,
            parentName: p.parentName ? String(p.parentName) : undefined,
            subIndex,
            relationshipText: p.relationshipText || relationshipText(p.relationshipType, p.parentName),
            isSubprofile,
          };
        }).filter((p): p is PersonRow => Boolean(p && p.id && p.name));
        setRows(mapped);
      } catch (e) {
        console.error('Suche fehlgeschlagen', e);
        setRows([]);
      } finally {
        setIsLoading(false);
      }
    };
    doSearch();
  }, [q, typeFilter, searchAllStandorte, standortId]);

  const submitSearch = (evt?: React.FormEvent) => {
    if (evt) evt.preventDefault();
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (typeFilter !== 'all') params.set('type', typeFilter);
    if (searchAllStandorte) params.set('all_standorte', '1');
    else if (standortId) params.set('standort_id', standortId);
    const basePath = location.pathname.startsWith('/system') ? '/system/dashboard/search' : '/dashboard/search';
    navigate({ pathname: basePath, search: params.toString() });
  };

  return <div className="p-4 md:p-6">
    <h2 className="mb-4 text-xl font-semibold">Suchergebnisse</h2>

    <form onSubmit={submitSearch} className="mb-4 flex flex-wrap items-center gap-2">
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Suchen..."
          className="pl-8 md:w-[260px] lg:w-[360px]"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
      </div>

      <Select value={typeFilter} onValueChange={setTypeFilter}>
        <SelectTrigger className="w-[160px]">
          <div className="flex items-center">
            <Filter className="mr-2 h-4 w-4" />
            <span>Personentyp</span>
          </div>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Alle Typen</SelectItem>
          <SelectItem value="teacher">Lehrkräfte</SelectItem>
          <SelectItem value="customer">Kunden</SelectItem>
          <SelectItem value="subprofile">Schülerprofile</SelectItem>
        </SelectContent>
      </Select>

      <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
        <span>In allen Standorten suchen</span>
        <Switch
          checked={searchAllStandorte}
          onCheckedChange={(checked) => setSearchAllStandorte(checked)}
        />
      </div>

      <Button type="submit">Suchen</Button>
    </form>

    <div className="overflow-x-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>ID</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>E-Mail</TableHead>
            <TableHead>Telefon</TableHead>
            <TableHead>Typ</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={5}>Lade...</TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5}>Keine Ergebnisse</TableCell>
            </TableRow>
          ) : rows.map(r => {
            const typeText = r.isSubprofile ? 'subprofile' : (r.type || '-');
            const typeBadge = typeText !== '-' ? <Badge variant="secondary">{typeText}</Badge> : '-';
            let path = `/dashboard/people/${r.id}`;
            if (r.isSubprofile && r.parentId) {
              const suffix = r.subIndex || Number(r.id.split('-').at(-1) || 1);
              path = subprofileDetailPath(r.parentId, suffix, location.pathname);
            } else {
              path = personDetailPath(r.id, location.pathname);
            }
            return (
              <TableRow key={`${r.id}-${r.parentId || 'root'}`} className="cursor-pointer" onClick={() => navigate(path)}>
                <TableCell>{r.id}</TableCell>
                <TableCell className="font-medium">
                  {r.name}
                  {r.relationshipText ? <div className="text-xs text-violet-700">{r.relationshipText}</div> : null}
                </TableCell>
                <TableCell>{r.email || '-'}</TableCell>
                <TableCell>{r.phone || '-'}</TableCell>
                <TableCell>{typeBadge}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  </div>;
};

export default SearchResults;


